import { WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { Stack, Button, Typography, Tooltip } from '@mui/material';
import { Form, Field, FormikHelpers, FormikProps, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { pick } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { tradersApi } from 'api';
import {
  DataWrapper,
  FormikNumericField,
  FormikYesNoRadioGroup,
  WorkStatusSelect,
  PageHeader,
  FormikCheckbox,
  CommissionIntervalForm,
  INTERVAL_COMMISSION_VALIDATION_SCHEMA,
  FormControls,
  TimeIntervalSelect,
} from 'components';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey, TraderWorkStatus } from 'enums';
import { useMeta, useMutation, useAppHistory, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Trader } from 'types';

type Values = Pick<
  Trader,
  | 'compensation'
  | 'intervalCommissions'
  | 'payoutCompensation'
  | 'workStatus'
  | 'unableTakeOrders'
  | 'unableTakeOrdersUntilAt'
  | 'insuranceDepositAmount'
  | 'payoutMaxActiveOrders'
  | 'insuranceDepositWithdrawalAllowed'
  | 'skipPayinRequisitesVerification'
  | 'payinMinOrderAmount'
  | 'payinMaxOrderAmount'
  | 'payoutHoldTimeout'
  | 'payoutMinOrderAmount'
  | 'payoutMaxOrderAmount'
  | 'payoutMaxDailyAmount'
  | 'payoutRequisitesAutomationEnabled'
  | 'payoutVolumeControlEnabled'
  | 'payoutVolumeMaxDeviation'
> & { name?: string; unableTakeOrdersDuration: number };

export const TraderDetailsPage: React.FC = () => {
  // TODO: move keys to common
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.traders',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common, {});

  const { role, isAdmin, isTechOperator } = useUser();
  const { isPayoutVolumeControlEnabled } = useMeta();

  const backUrl = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.TRADERS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.TRADERS;
    }
  }, [isAdmin, isTechOperator]);

  const { id } = useParams();

  const { goBack } = useAppHistory();

  const [initialValues, setInitialValues] = useState<Values>({
    name: '',
    compensation: 0,
    intervalCommissions: [],
    payoutCompensation: 0,
    workStatus: TraderWorkStatus.Inactive,
    unableTakeOrders: false,
    unableTakeOrdersUntilAt: '',
    unableTakeOrdersDuration: 0,
    insuranceDepositAmount: 0,
    payoutMaxActiveOrders: 0,
    payinMinOrderAmount: 0,
    payinMaxOrderAmount: 0,
    payoutMinOrderAmount: 0,
    payoutMaxOrderAmount: 0,
    payoutMaxDailyAmount: 0,
    skipPayinRequisitesVerification: false,
    insuranceDepositWithdrawalAllowed: false,
    payoutRequisitesAutomationEnabled: false,
    payoutHoldTimeout: 0,
    payoutVolumeControlEnabled: false,
    payoutVolumeMaxDeviation: 0,
  });

  const queryResult = useQuery(
    [QueryKey.Traders, id],
    () => tradersApi.findOneAsRole(role)(id as string),
    {
      onSuccess: (data) => {
        setInitialValues({
          name: data.user?.name,
          compensation: data.compensation,
          intervalCommissions: data.intervalCommissions || [],
          payoutCompensation: data.payoutCompensation,
          workStatus: data.workStatus,
          unableTakeOrders: data.unableTakeOrders,
          unableTakeOrdersDuration: 0,
          unableTakeOrdersUntilAt: data.unableTakeOrdersUntilAt,
          skipPayinRequisitesVerification:
            !!data.skipPayinRequisitesVerification,
          insuranceDepositAmount: data.insuranceDepositAmount,
          payoutMaxActiveOrders: data.payoutMaxActiveOrders,
          insuranceDepositWithdrawalAllowed:
            data.insuranceDepositWithdrawalAllowed,
          payinMinOrderAmount: data.payinMinOrderAmount,
          payinMaxOrderAmount: data.payinMaxOrderAmount,
          payoutHoldTimeout: data.payoutHoldTimeout,
          payoutMinOrderAmount: data.payoutMinOrderAmount,
          payoutMaxOrderAmount: data.payoutMaxOrderAmount,
          payoutMaxDailyAmount: data.payoutMaxDailyAmount,
          payoutRequisitesAutomationEnabled:
            data.payoutRequisitesAutomationEnabled,
          payoutVolumeControlEnabled: !!data.payoutVolumeControlEnabled,
          payoutVolumeMaxDeviation: data.payoutVolumeMaxDeviation ?? 0,
        });
      },
    },
  );

  const { mutate: updateTrader } = useMutation(tradersApi.updateByRole(role));

  const navigateBack = useCallback(() => goBack(backUrl), [goBack, backUrl]);

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      const trader = {
        ...pick(values, [
          'compensation',
          'intervalCommissions',
          'payoutCompensation',
          'workStatus',
          'unableTakeOrders',
          'insuranceDepositAmount',
          'payoutMaxActiveOrders',
          'insuranceDepositWithdrawalAllowed',
          'skipPayinRequisitesVerification',
          'payinMinOrderAmount',
          'payinMaxOrderAmount',
          'payoutHoldTimeout',
          'payoutMinOrderAmount',
          'payoutMaxOrderAmount',
          'payoutMaxDailyAmount',
          'payoutRequisitesAutomationEnabled',
          'payoutVolumeControlEnabled',
          'payoutVolumeMaxDeviation',
        ]),
        unableTakeOrdersDuration: Number(values.unableTakeOrdersDuration),
      };

      updateTrader(
        { id: id as string, trader },
        {
          onSuccess: navigateBack,
          onSettled: () => formikHelpers.setSubmitting(false),
        },
      );
    },
    [updateTrader, id, navigateBack],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        intervalCommissions: INTERVAL_COMMISSION_VALIDATION_SCHEMA,
      }),
    [],
  );

  const canUpdateUnableTakeOrdersTimeInterval = useMemo(
    () =>
      !initialValues.unableTakeOrders && !initialValues.unableTakeOrdersUntilAt,
    [initialValues],
  );

  const handleUnableTakeOrdersChange = useCallback(
    (formik: FormikProps<Values>) => {
      formik.setFieldValue('unableTakeOrdersDuration', 0);
    },
    [],
  );

  return (
    <DataWrapper queryResult={queryResult}>
      <div className="tw-max-w-md">
        <PageHeader title={t('details_title')} />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik: FormikProps<Values>) => (
            <Form>
              <Stack spacing={4}>
                <Field
                  component={TextField}
                  label={t('fields.name')}
                  size="small"
                  name="name"
                  disabled
                />
                <WorkStatusSelect />
                <FormControls sx={{ pb: 5 }}>
                  <div className="tw-flex tw-items-center">
                    <FormikCheckbox
                      name="unableTakeOrders"
                      label={t('fields.unable_take_orders')}
                      onChange={() => handleUnableTakeOrdersChange(formik)}
                    />
                    {!canUpdateUnableTakeOrdersTimeInterval && (
                      <Tooltip
                        title={t('fields.unable_take_orders_period_update')}
                      >
                        <WarningAmberIcon color="warning" />
                      </Tooltip>
                    )}
                  </div>
                  {canUpdateUnableTakeOrdersTimeInterval && (
                    <TimeIntervalSelect
                      name="unableTakeOrdersDuration"
                      label={t('fields.unable_take_orders_period')}
                      disabled={!formik.values.unableTakeOrders}
                    />
                  )}
                </FormControls>
                <Stack direction="column" spacing={3}>
                  <FormikNumericField
                    name="compensation"
                    label={t('fields.payin_compensation')}
                    allowNegative={false}
                    percentageSuffix
                    required
                    fullWidth
                  />
                  <CommissionIntervalForm name="intervalCommissions" />
                  <FormikNumericField
                    name="payoutCompensation"
                    label={t('fields.payout_compensation')}
                    allowNegative={false}
                    percentageSuffix
                    required
                    fullWidth
                  />
                </Stack>
                <FormikNumericField
                  name="insuranceDepositAmount"
                  label={t('fields.insurance_deposit_amount')}
                  allowNegative={false}
                  decimalScale={0}
                  fullWidth
                />
                <FormikYesNoRadioGroup
                  name="insuranceDepositWithdrawalAllowed"
                  label={t('fields.insurance_deposit_withdrawal_allowed')}
                />
                <Typography variant="h6">{tCommon('common.payin')}</Typography>
                <Stack direction="row" spacing={3}>
                  <FormikNumericField
                    name="payinMinOrderAmount"
                    label={t('fields.min_order_amount')}
                    allowNegative={false}
                    defaultValue={0}
                    fullWidth
                  />
                  <FormikNumericField
                    name="payinMaxOrderAmount"
                    label={t('fields.max_order_amount')}
                    allowNegative={false}
                    defaultValue={0}
                    fullWidth
                  />
                </Stack>
                <FormikCheckbox
                  name="skipPayinRequisitesVerification"
                  label={t('fields.skip_requisites_verification')}
                />
                <Typography variant="h6">{tCommon('common.payout')}</Typography>
                <Stack direction="row" spacing={3}>
                  <FormikNumericField
                    name="payoutMinOrderAmount"
                    label={t('fields.min_order_amount')}
                    allowNegative={false}
                    defaultValue={0}
                    fullWidth
                  />
                  <FormikNumericField
                    name="payoutMaxOrderAmount"
                    label={t('fields.max_order_amount')}
                    allowNegative={false}
                    defaultValue={0}
                    fullWidth
                  />
                </Stack>
                <FormikNumericField
                  name="payoutHoldTimeout"
                  label={t('fields.payout_hold_timeout')}
                  allowNegative={false}
                  decimalScale={0}
                  defaultValue={0}
                  fullWidth
                />
                <FormikNumericField
                  name="payoutMaxDailyAmount"
                  label={t('fields.max_daily_amount')}
                  allowNegative={false}
                  defaultValue={0}
                  fullWidth
                />
                <FormikNumericField
                  name="payoutMaxActiveOrders"
                  label={t('fields.max_active_orders')}
                  allowNegative={false}
                  decimalScale={0}
                  defaultValue={0}
                  fullWidth
                />
                <FormikCheckbox
                  name="payoutRequisitesAutomationEnabled"
                  label={t('fields.payout_requisites_automation_enabled')}
                />
                {isPayoutVolumeControlEnabled && (
                  <FormikCheckbox
                    name="payoutVolumeControlEnabled"
                    label={t('fields.payout_volume_control_enabled')}
                  />
                )}
                {isPayoutVolumeControlEnabled &&
                  formik.values.payoutVolumeControlEnabled && (
                    <FormikNumericField
                      name="payoutVolumeMaxDeviation"
                      label={t('fields.payout_volume_max_deviation')}
                      allowNegative={false}
                      defaultValue={0}
                      suffix="USDT"
                      fullWidth
                    />
                  )}
                <Stack direction="row" justifyContent="end" spacing={3}>
                  <Button variant="outlined" onClick={navigateBack}>
                    {tCommon('buttons.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={formik.isSubmitting}
                    onClick={formik.submitForm}
                  >
                    {tCommon('buttons.save')}
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </div>
    </DataWrapper>
  );
};
